<script setup lang="ts">
import { ref, watchEffect, computed } from 'vue';
import { AkButton, AkModal } from '@ankorstore/design-system';
import { QuantityCounter } from '@bc/shared/components';
import type { ProductOutputData } from '../services/product-builder';
import AddToCartPopin from '@monolith/legacy/components/popins/add-to-cart-popin.vue';
import { getDiscount, getPriceBreakdown } from '@client/monolith/legacy/services/utils/tooling';
import usePrice from '@client/monolith/core/composables/use-price';
import { useCart } from '@client/monolith/legacy/modules/cart-2/composables/use-cart';
import { captureRetailerCoreDiscoveryException } from '@core/plugins/sentry/helper';
import { useI18n } from 'vue-i18n';
import { getVariant } from '@client/monolith/legacy/services/features';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import RootStoreHelpers from '@monolith/legacy/store/helpers';
import VatForm from '@monolith/legacy/components/vat-form.vue';
import { useTemplateRef } from 'vue';
import { useAuthPopin } from '@core/composables/use-auth-popin';
import { RetailerFormType } from '@monolith/legacy/types/retailer-form-type';

const { getConvertedPrice } = usePrice();
const { updateItemQuantity, ensureCart } = useCart();
const { openAuthPopin } = useAuthPopin();

type ProductViewModel = ProductOutputData;

const props = withDefaults(
  defineProps<{
    product: ProductViewModel;
    /** When false, the parent owns the bottom spacing and this component drops its margin-top: auto. */
    autoMarginTop?: boolean;
  }>(),
  {
    autoMarginTop: true,
  }
);
const quickAddToCartModal = ref<DS['AkModal']>(null);
const { t } = useI18n();
const priceBreakdown = ref<ReturnType<typeof getPriceBreakdown>>();
const { userIsRetailer, canGetWholesalePrice, userIsBrand } = RootStoreHelpers.useGetters([
  'userIsRetailer',
  'userIsBrand',
  'canGetWholesalePrice',
]);
const isLoading = ref(false);
const confirmation = ref(false);
const missingVatModal = useTemplateRef<InstanceType<typeof AkModal>>('missingVatModal');

const emit = defineEmits(['addedToCart', 'variantAddedToCart', 'addToCartClicked']);

const defaultQuantity = getVariant('RET-3605') === 'A' ? 0 : props.product.unitMultiplier;
const min = getVariant('RET-3605') === 'A' ? 0 : props.product.unitMultiplier;

const userGuard = (): boolean => {
  if (!userIsRetailer.value) {
    openAuthPopin(RetailerFormType.Register);
    return false;
  }
  if (!canGetWholesalePrice.value) {
    missingVatModal.value.openModal();
    return false;
  }
  return true;
};

const addToCart = async (event) => {
  emit('addToCartClicked', event);
  if (await !userGuard()) {
    return false;
  }
  const payload = {
    brand: props.product.brand,
    option: { id: props.product.id, name: props.product.name as string },
    variant: null,
    quantity: quantity.value / props.product.unitMultiplier,
  };
  isLoading.value = true;
  try {
    await ensureCart();
    updateItemQuantity(props.product.variants[0].uuid, quantity.value / props.product.unitMultiplier, true);
  } catch (error) {
    captureRetailerCoreDiscoveryException(error);
  } finally {
    isLoading.value = false;
    confirmation.value = true;
    setTimeout(() => {
      confirmation.value = false;
      emit('addedToCart', payload);
      quantity.value = defaultQuantity;
    }, 1000);
  }
  return true;
};

const color = computed(() => {
  return getVariant('RET-3605') === 'B' ? 'white' : 'primary';
});

const outlined = computed(() => {
  return getVariant('RET-3605') === 'B';
});

const cartSymbolVisible = computed(() => {
  return !isLoading.value ? 'cart' : '';
});

const addToCartVariant = async () => {
  if (await !userGuard()) {
    return;
  }
  quickAddToCartModal.value.openModal();
};

watchEffect(async () => {
  priceBreakdown.value = getPriceBreakdown(
    await getConvertedPrice(props.product.originalWholeSalePrice),
    await getConvertedPrice(props.product.retail_price),
    await getConvertedPrice(props.product.wholeSalePrice),
    getDiscount(props.product.discountPercentage, props.product.discountType)
  );
});

const quantity = ref(defaultQuantity);

</script>

<template>
  <div
    v-if="!product.outOfStock"
    class="qatcContainer"
    :class="{ 'qatcContainer--auto-margin': autoMarginTop }"
  >
    <div v-if="product.variants.length <= 1">
      <div
        v-if="!confirmation"
        class="qatcContainer__withoutVariant"
      >
        <QuantityCounter
          v-model="quantity"
          :step="product.unitMultiplier"
          :min="min"
          :disabled="userIsBrand"
          :tracking-context="{ source: 'catalog_card', productId: product.id }"
          class="qatcContainer__withoutVariant--counter"
        />
        <AkButton
          data-testid="addToCartButton"
          size="lg"
          :color="color"
          :symbol="cartSymbolVisible"
          :outlined="outlined"
          :disabled="quantity === 0 || userIsBrand"
          @click="addToCart"
        >
          <LoaderIcon
            v-if="isLoading"
            :dark="color === 'primary'"
            :small="true"
          />
        </AkButton>
      </div>
      <div
        v-else
        class="qatcContainer__withoutVariant"
      >
        <AkButton
          size="lg"
          color="success"
          class="ds-w-full"
          data-testid="confirmationButton"
        >
          <AkIcon
            size="md"
            symbol="check"
            color="white"
            class="ds-mr-2"
          />
          {{ quantity }} {{ t('discovery.qatc.added') }}
        </AkButton>
      </div>
    </div>
    <div
      v-else
      class="qatcContainer__withVariant"
    >
      <AkButton
        color="white"
        outlined
        size="lg"
        type="button"
        class="qatcContainer__withVariant--button"
        :disabled="userIsBrand"
        data-testid="addToCartButton"
        @click="addToCartVariant"
      >
        <AkIcon
          size="md"
          symbol="cart"
          class="ds-mr-2"
        />{{ t('discovery.qatc.selectOptions') }}
      </AkButton>
      <Teleport to="body">
        <AkModal
          ref="quickAddToCartModal"
          size="xl"
          :with-padding="false"
          data-testid="addToCartModal"
        >
          <AddToCartPopin
            :product-id="product.id"
            :price-breakdown="priceBreakdown"
            @add-to-cart-variant="emit('variantAddedToCart', $event)"
          />
        </AkModal>
      </Teleport>
    </div>
    <Teleport to="body">
      <AkModal
        ref="missingVatModal"
        size="xl"
      >
        <VatForm
          from-add-cart
          data-testid="missingVatModal"
          @success="missingVatModal.close()"
          @close="missingVatModal.close()"
        />
      </AkModal>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.qatcContainer {
  &--auto-margin {
    @apply ds-mt-auto;
  }
  &__withoutVariant {
    @apply ds-flex ds-gap-2 ds-mt-3;
    align-items: center;
    justify-content: center;
    &--counter {
      @apply ds-min-w-[40px];
    }
    button {
      @apply ds-relative;
      .loader-icon {
        @apply ds-relative;
        top: 0;
        left: 0;
      }
    }
  }
  &__withVariant {
    @apply ds-mt-3;
    &--button {
      @apply ds-w-full;
    }
    :deep(.ak-button) {
      @apply ds-text-wrap md:ds-text-nowrap ds-leading-[1rem];
    }
  }
}
</style>
